<template>
  <component :is="layoutComponent" v-bind="layoutProps">
    <template slot="field">
      <div class="autocomplete-field" :class="errorClasses">
        <input
          :id="field.attribute"
          ref="input"
          v-model="inputValue"
          type="text"
          class="form-control"
          :placeholder="placeholder"
          :disabled="isDisabled"
          autocomplete="off"
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
        />

        <div v-if="isOpen" class="autocomplete-dropdown" @mousedown.prevent>
          <div v-if="loading" class="autocomplete-loading">
            {{ $t('general.loading') }}
          </div>
          <div v-else-if="showNoOptionsPlaceholder" class="autocomplete-empty">
            {{ strings.relation.no_options }}
          </div>
          <div v-else class="autocomplete-options">
            <div
              v-for="(option, index) in options"
              :key="option.value"
              class="autocomplete-option"
              @mousedown.prevent="selectOption(option)"
            >
              <div class="autocomplete-option-label">{{ option.label }}</div>
              <div v-if="option.extra" class="autocomplete-option-extra">
                {{ option.extra }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </component>
</template>

<script>
import debounce from 'lodash-es/debounce'
import get from 'lodash-es/get'
import { FieldMixin } from 'stylemix-base/mixins'

export default {
  name: 'AutocompleteField',

  mixins: [FieldMixin],

  data() {
    return {
      options: [],
      isOpen: false,
      loading: false,
      hasSelection: false,
      inputValue: '',
      blurTimeout: null,
      lastRequestId: 0,
      emptyPrefix: null,
    }
  },

  computed: {
    minLength() {
      return this.field.minLength || 3
    },

    params() {
      return this.field.source ? this.field.source.params : {}
    },

    placeholder() {
      return this.field.placeholder ?? this.$t('general.start_type_username')
    },

    showNoOptionsPlaceholder() {
      return this.options.length === 0
    },
  },

  watch: {
    fieldValue: {
      immediate: true,
      handler(newValue) {
        if (!this.hasSelection && newValue !== this.inputValue) {
          this.inputValue = newValue || ''
        }
      },
    },
  },

  created() {
    this.search = debounce(() => {
      const requestId = ++this.lastRequestId

      const queryParam =
        this.field.queryParam || this.field.source?.queryParam || 'query'

      this.$http({
        url: this.field.source.url,
        params: { ...this.params, [queryParam]: this.inputValue },
      })
        .then((response) => {
          if (this.lastRequestId !== requestId) {
            return
          }

          this.options = this.transformRemoteOptions(response.data)

          if (this.options.length === 0) {
            this.emptyPrefix = this.inputValue
          } else {
            this.emptyPrefix = null
          }
        })
        .finally(() => {
          if (this.lastRequestId === requestId) {
            this.loading = false
          }
        })
    }, 500)
  },

  beforeDestroy() {
    this.search.cancel()
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout)
    }
  },

  mounted() {
    this.inputValue = this.fieldValue || ''
  },

  methods: {
    onInput() {
      const previousValue = this.fieldValue
      this.fieldValue = this.inputValue

      if (this.inputValue.length < (previousValue?.length || 0)) {
        this.emptyPrefix = null
      }

      if (this.emptyPrefix && this.inputValue.startsWith(this.emptyPrefix)) {
        return
      }

      if (this.inputValue.length >= this.minLength) {
        this.isOpen = true
        this.loading = true
        this.search()
      } else {
        this.loading = false
        this.isOpen = false
        this.emptyPrefix = null
      }
    },

    onFocus() {
      if (this.blurTimeout) {
        clearTimeout(this.blurTimeout)
        this.blurTimeout = null
      }
    },

    onBlur() {
      this.blurTimeout = setTimeout(() => {
        this.isOpen = false
      }, 150)
    },

    onKeydown(event) {
      if (!this.isOpen && event.key === 'Enter') {
        event.preventDefault()
        if (this.inputValue.length >= this.minLength) {
          this.search()
        }
        return
      }

      if (event.key === 'Escape') {
        this.isOpen = false
      }
    },

    selectOption(option) {
      if (this.blurTimeout) {
        clearTimeout(this.blurTimeout)
        this.blurTimeout = null
      }

      this.hasSelection = true
      this.inputValue = option.label
      this.fieldValue = option.value
      this.isOpen = false

      this.emitSelect(option._raw)
    },

    transformRemoteOptions(data) {
      const resultKey = this.field.source?.resultKey || 'data'
      const valueKey = this.field.source?.valueKey || 'value'
      const labelKey = this.field.source?.labelKey || 'label'
      const extraKey = this.field.source?.extraKey || 'extra'
      const options = get(data, resultKey)

      if (!(options instanceof Array)) {
        // eslint-disable-next-line no-console
        console.error(
          'AutocompleteField: returned options is not an Array. Please, check your configuration and ajax response.'
        )
        return []
      }

      return options.map((item) => ({
        value: get(item, valueKey),
        label: get(item, labelKey),
        extra: get(item, extraKey),
        _raw: item,
      }))
    },

    emitSelect(value) {
      this.$emit('select', value)

      // hack because no event bubbling
      this.formResolved.$emit('select:' + this.field.attribute, value)
    },
  },
}
</script>

<style lang="scss" scoped>
.autocomplete-field {
  position: relative;
}

.autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid #ddd;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
}

.autocomplete-loading,
.autocomplete-empty {
  padding: 10px 15px;
  color: #666;
  font-size: 14px;
}

.autocomplete-option {
  padding: 10px 15px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f5f5f5;
  }
}

.autocomplete-option-label {
  font-size: 14px;
  color: #333;
}

.autocomplete-option-extra {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}
</style>
